<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card class="px-3 mt-4 pt-7">
          <SimplePageHeader :title="pageTitle" class="px-4 pr-5 mb-5"/>
          <v-card-text>
            <v-form ref="detailsForm">
              <h2 class="mb-4">Details</h2>
              <v-row>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="name"
                    label="Template Name *"
                    outlined
                    :rules="[Required('Template Name')]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-switch v-model="isActive" label="Active" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <ClientsDropDownList
                    :id="clientId"
                    :clearable="true"
                    @clientIdChange="clientId = $event"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="recipientSourceId"
                    :items="recipientSourceOptions"
                    item-text="name"
                    item-value="id"
                    label="Recipient Source *"
                    outlined
                    :rules="[Required('Recipient Source')]"
                  />
                </v-col>
              </v-row>
              <v-row v-if="recipientSourceId === workflowEmailRecipientSource.Custom">
                <v-col cols="12">
                  <v-textarea
                    v-model="customRecipients"
                    label="Custom Recipients"
                    hint="Comma-separated email addresses"
                    persistent-hint
                    outlined
                    rows="2"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-textarea
                    v-model="additionalRecipients"
                    label="Additional Recipients"
                    hint="Comma-separated email addresses, always included"
                    persistent-hint
                    outlined
                    rows="2"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-textarea
                    v-model="reviewerEmails"
                    label="Reviewer Emails"
                    hint="Falls back to global Ops Email Addresses if left blank"
                    persistent-hint
                    outlined
                    rows="2"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="subjectLine"
                    label="Subject Line *"
                    outlined
                    :rules="[Required('Subject Line')]"
                  />
                </v-col>
              </v-row>
            </v-form>

            <h2 class="mb-4">Email Content</h2>
            <v-row>
              <v-col cols="12" md="9">
                <quill-editor
                  ref="quillEditor"
                  v-model="bodyHtml"
                  :options="quillOptions"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined>
                  <v-card-title class="subtitle-1">Insert Token</v-card-title>
                  <v-card-subtitle>Click a token to insert it at the cursor position</v-card-subtitle>
                  <v-list dense class="token-list">
                    <v-list-item
                      v-for="token in tokens"
                      :key="token.name"
                      @click="insertToken(token.name)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ '{' + token.name + '}' }}</v-list-item-title>
                        <v-list-item-subtitle>{{ token.description }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>

            <div class="mb-4">
              <h2 class="mb-2">Attachments</h2>
              <p class="grey--text">Reusable library files always attached to this email. To attach a document generated earlier in a workflow, configure that on the automated-email task itself.</p>
              <v-row v-for="(attachment, index) in attachments" :key="index" align="center">
                <v-col cols="12" sm="8">
                  <WorkflowLibraryAssetDropDownList
                    :id="attachment.libraryAssetId"
                    :isRequired="true"
                    @assetIdChange="attachment.libraryAssetId = $event"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="d-flex align-center">
                  <v-btn icon :disabled="index === 0" @click="moveAttachment(index, -1)">
                    <v-icon>arrow_upward</v-icon>
                  </v-btn>
                  <v-btn icon :disabled="index === attachments.length - 1" @click="moveAttachment(index, 1)">
                    <v-icon>arrow_downward</v-icon>
                  </v-btn>
                  <v-btn icon color="red" @click="removeAttachment(index)">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-btn outlined color="primary" small @click="addAttachment">
                <v-icon small left>add</v-icon>Add Attachment
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <ButtonsSubmitForm
          v-if="!isReadOnlyAccess"
          :isFormValid="true"
          @save="handleSave"
          @cancel="cancel"
          />
      </v-col>
    </v-row>
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { workflowEmailTemplatesRoute } from '@/lib/constants/routes'
import { workflowEmailRecipientSource, WORKFLOW_EMAIL_RECIPIENT_SOURCE_OPTIONS } from '@/lib/constants/workflowEmailStatuses'
import { SimplePageHeader } from '@/components/pageComponents'
import { ButtonsSubmitForm, BottomSpacer } from '@/components/buttons'
import { ClientsDropDownList } from '@/components/clients'
import WorkflowLibraryAssetDropDownList from '@/components/workflowLibraryAssets/WorkflowLibraryAssetDropDownList'
import Required from '@/lib/formValidations/required'

const WORKFLOW_EMAIL_TOKENS = [
  { name: 'CandidateFirstName', description: 'Candidate\'s first name' },
  { name: 'CandidateLastName', description: 'Candidate\'s last name' },
  { name: 'CandidateFullName', description: 'Candidate\'s full name' },
  { name: 'CandidateEmail', description: 'Candidate\'s email address' },
  { name: 'ClientName', description: 'Client company name' },
  { name: 'JobTitle', description: 'Job title for the assignment/placement' },
  { name: 'StartDate', description: 'Assignment or placement start date' },
  { name: 'EndDate', description: 'Assignment end date' },
  { name: 'ContractType', description: 'Contract type' },
  { name: 'PayRate', description: 'Pay rate' },
  { name: 'BillRate', description: 'Bill rate' },
  { name: 'PlacementDate', description: 'Direct placement date' },
  { name: 'PlacementSalary', description: 'Direct placement salary' },
  { name: 'RecruiterName', description: 'Recruiter\'s name' },
  { name: 'CompanyName', description: 'CTI company name' },
  { name: 'CompanyAddress', description: 'CTI company address' },
  { name: 'TodayDate', description: 'Today\'s date' }
]

export default {
  components: {
    SimplePageHeader,
    ButtonsSubmitForm,
    BottomSpacer,
    ClientsDropDownList,
    WorkflowLibraryAssetDropDownList
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },
  data () {
    return {
      workflowEmailTemplatesRoute,
      workflowEmailRecipientSource,
      recipientSourceOptions: WORKFLOW_EMAIL_RECIPIENT_SOURCE_OPTIONS,
      tokens: WORKFLOW_EMAIL_TOKENS,
      quillOptions: {
        theme: 'snow'
      },
      previousRoute: null
    }
  },
  computed: {
    ...mapFields('workflowEmailTemplate', [
      'id',
      'name',
      'clientId',
      'subjectLine',
      'bodyHtml',
      'recipientSourceId',
      'customRecipients',
      'additionalRecipients',
      'reviewerEmails',
      'isActive',
      'attachments'
    ]),
    isEditing () {
      return !!this.$route.params.id
    },
    templateIdAsInt () {
      return parseInt(this.$route.params.id) || this.id
    },
    pageTitle () {
      return this.isEditing ? 'Edit Workflow Email Template' : 'New Workflow Email Template'
    },
    saveButtonLabel () {
      return this.isEditing ? 'Update' : 'Save'
    }
  },
  methods: {
    ...mapActions('workflowEmailTemplate', ['getWorkflowEmailTemplate', 'saveWorkflowEmailTemplate', 'clearWorkflowEmailTemplate']),
    Required,
    handleSave () {
      if (!this.$refs.detailsForm.validate()) return
      this.saveWorkflowEmailTemplate()
        .then(response => {
          if (response.data) {
            this.$router.push(workflowEmailTemplatesRoute.url)
          }
        })
        .catch(() => {})
    },
    cancel () {
      if (this.previousRoute && this.previousRoute.name === workflowEmailTemplatesRoute.name) {
        this.$router.back()
        return
      }
      this.$router.push({ name: workflowEmailTemplatesRoute.name })
    },
    insertToken (tokenName) {
      const editor = this.$refs.quillEditor && this.$refs.quillEditor.quill
      if (!editor) return
      const range = editor.getSelection()
      const index = range ? range.index : editor.getLength()
      editor.insertText(index, `{${tokenName}}`)
      editor.setSelection(index + tokenName.length + 2)
    },
    addAttachment () {
      this.attachments = [...this.attachments, { libraryAssetId: null, sortOrder: this.attachments.length }]
    },
    removeAttachment (index) {
      this.attachments = this.attachments.filter((a, i) => i !== index)
    },
    moveAttachment (index, direction) {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= this.attachments.length) return
      const items = [...this.attachments]
      const [moved] = items.splice(index, 1)
      items.splice(newIndex, 0, moved)
      this.attachments = items.map((a, i) => ({ ...a, sortOrder: i }))
    }
  },
  created () {
    if (this.isEditing) {
      this.getWorkflowEmailTemplate({ id: this.$route.params.id })
    } else {
      this.clearWorkflowEmailTemplate()
    }
  }
}
</script>

<style scoped>
.token-list {
  max-height: 480px;
  overflow-y: auto;
}
</style>
